/// <amd-module name="Comments/Reactions/Components/CommentReactionStatusBar" />
import { CommentReactionType } from "Comments/Common/Contracts";
import { CommentReactionSelector } from "Comments/Reactions/Components/CommentReactionSelector";
import { ICommentReactionStatusBarProps } from "Comments/Reactions/Components/CommentReactionStatusBar.Props";
import { ReactionTypeToEmojiMap } from "Comments/Reactions/Constants";
import { IReactionEmojiInfo } from "Comments/Reactions/Contracts";
import * as ReactionResources from "Comments/Reactions/Resources";
import * as React from "react";
import * as StringUtils from "VSS/Core/Util/String";
import { css, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { ITooltipProps } from "VSSUI/TooltipEx";

const MaxUsersToDisplay: number = 99;

export class CommentReactionStatusBar extends React.PureComponent<ICommentReactionStatusBarProps> {
    private static readonly ReactionStatusButton: string = "reaction-status-button";

    public render(): JSX.Element {
        const { commentId, readonly, reactionTypeToInfoMap, onAddReactionButtonClick } = this.props;

        const buttons = this._getButtons();
        if (buttons.length > 0) {
            const setSize = buttons.length + (readonly ? 0 : 1);
            return (
                <div>
                    <FocusZone direction={FocusZoneDirection.Horizontal}>
                        <div className="reaction-status-container" role="menubar">
                            {buttons}
                            <CommentReactionSelector
                                className={css(CommentReactionStatusBar.ReactionStatusButton, "reaction-status-new-selector-btn")}
                                readonly={readonly}
                                commentId={commentId}
                                onAddReactionButtonClick={onAddReactionButtonClick}
                                onAddReaction={this._onAddReactionFromSelector}
                                onDeleteReaction={this._onDeleteReactionFromSelector}
                                reactionTypeToInfoMap={reactionTypeToInfoMap}
                                ariaPosInSet={setSize}
                                ariaSetSize={setSize}
                                excludeTabStop
                            />
                        </div>
                    </FocusZone>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private _getButtons(): JSX.Element[] {
        const { readonly, reactionTypeToInfoMap } = this.props;

        const result: JSX.Element[] = [];
        const items = Object.keys(reactionTypeToInfoMap)
            .map(reactionType => Number(reactionType))
            .filter(reactionTypeNum => {
                const info = reactionTypeToInfoMap[reactionTypeNum];
                return !!info && info.totalCount > 0;
            });
        const setSize = items.length + (readonly ? 0 : 1); // includes the selector
        let currentPosInSet = 1;
        for (const reactionTypeNum of items) {
            const { totalCount, isCurrentUserEngaged } = reactionTypeToInfoMap[reactionTypeNum];

            const emojiInfo: IReactionEmojiInfo = ReactionTypeToEmojiMap[reactionTypeNum];
            const reactionCountToDisplay: string = totalCount > MaxUsersToDisplay ? ReactionResources.MoreThan99 : totalCount.toString();
            result.push(
                <Button
                    key={reactionTypeNum}
                    className={css(
                        CommentReactionStatusBar.ReactionStatusButton,
                        readonly && "reaction-status-button-readonly",
                        isCurrentUserEngaged && "reaction-status-user-engaged"
                    )}
                    tooltipProps={readonly ? undefined : this._getTooltip(reactionTypeNum, totalCount, emojiInfo)}
                    ariaLabel={StringUtils.format(
                        isCurrentUserEngaged ? ReactionResources.RemoveReaction : ReactionResources.AddReaction,
                        emojiInfo.description
                    )}
                    onClick={readonly ? undefined : this._onReactionClick.bind(this, reactionTypeNum, isCurrentUserEngaged)}
                    excludeTabStop={result.length > 0}
                    role="menuitem"
                    ariaPosInSet={currentPosInSet++}
                    ariaSetSize={setSize}
                >
                    <span className="reaction-status-info-container">
                        <span dangerouslySetInnerHTML={{ __html: emojiInfo.encodedValue }} />
                        <span>{reactionCountToDisplay}</span>
                    </span>
                </Button>
            );
        }

        return result;
    }

    private _onReactionClick(reactionTypeNum: number, isCurrentUserEngaged: boolean): void {
        const { commentId, onAddReaction, onDeleteReaction } = this.props;

        if (isCurrentUserEngaged) {
            onDeleteReaction(commentId, reactionTypeNum, true);
        } else {
            onAddReaction(commentId, reactionTypeNum, true);
        }
    }

    private _getTooltip(reactionTypeNum: number, totalCount: number, emojiInfo: IReactionEmojiInfo): ITooltipProps {
        return {
            renderContent: this._onRenderButtonTooltipContent.bind(this, reactionTypeNum, totalCount, emojiInfo),
            showOnFocus: true
        };
    }

    private _onRenderButtonTooltipContent(reactionTypeNum: number, totalCount: number, emojiInfo: IReactionEmojiInfo): JSX.Element {
        const { commentId, onGetRecentlyReactedUsers, reactionTypeToInfoMap } = this.props;

        const reactionTypeRecentUsers: { [reactionType: number]: string[] } = Object.keys(reactionTypeToInfoMap || {}).reduce(
            (resultObj: { [reactionType: number]: string[] }, reactionType) => {
                const reactionTypeNum = parseInt(reactionType);
                resultObj[reactionTypeNum] = reactionTypeToInfoMap[reactionTypeNum].engagedUsers.map(
                    identityInfo => identityInfo.identityDisplayName
                );
                return resultObj;
            },
            {}
        );

        const displayNames = reactionTypeRecentUsers[reactionTypeNum];
        let usersDisplayInTooltip: JSX.Element[] = [];
        if (displayNames && displayNames.length > 0) {
            // Limit the number of users displayed in the tooltip to the count of the reactions as these two values can go out of step because there
            // is no api that returns both the values in a single call.
            usersDisplayInTooltip = displayNames.slice(0, totalCount).map((displayName, index) => <li key={index}>{displayName}</li>);

            const additionalUsersToolTipCount = totalCount - 10;

            if (additionalUsersToolTipCount > 0) {
                usersDisplayInTooltip.push(
                    <li className="userlist-item" key={"nmoreusers"}>
                        {StringUtils.format(ReactionResources.ReactionAdditionalUsersCount, additionalUsersToolTipCount)}
                    </li>
                );
            }
        } else {
            onGetRecentlyReactedUsers(commentId, reactionTypeNum, MaxUsersToDisplay + 1); // See if there are more users than the max users to display
        }

        if (usersDisplayInTooltip.length > 0) {
            return (
                <div className="userlist-tooltip">
                    <h3>
                        {StringUtils.format(
                            usersDisplayInTooltip.length > 1
                                ? ReactionResources.ReactionUserListHeaderPlural
                                : ReactionResources.ReactionUserListHeaderSingular,
                            emojiInfo.description
                        )}
                    </h3>
                    <ul className="user-list">{usersDisplayInTooltip}</ul>
                </div>
            );
        }
        return <></>; // display empty tooltip while waiting
    }

    private _onAddReactionFromSelector = (commentId: number, reactionType: CommentReactionType): void => {
        this.props.onAddReaction(commentId, reactionType, false);
    };

    private _onDeleteReactionFromSelector = (commentId: number, reactionType: CommentReactionType): void => {
        this.props.onDeleteReaction(commentId, reactionType, false);
    };
}

VssComponent.register("comment-reaction-statusbar", CommentReactionStatusBar);
